import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useProfile } from '../../hooks/useProfile';
import { useNotificationStatsQuery } from '../../hooks/useNotificationQueries';
import ExitImpersonationConfirmModal from '../modals/ExitImpersonationConfirmModal/ExitImpersonationConfirmModal';
import './Header.css';

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, originalUser, stopImpersonating } = useAuth();
  const { data: profile } = useProfile();
  const { data: notificationStats } = useNotificationStatsQuery();
  const [isExitImpersonationModalOpen, setIsExitImpersonationModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  const unreadCount = notificationStats?.unread ?? 0;
  const avatar = profile?.avatar || user?.avatar || '/avatar.png';
  const isImpersonating = !!originalUser;
  const hasAdminAccess = Boolean(profile?.is_system_admin) || Boolean(profile?.is_user_manager);

  const hasPermission = (permissionSlug: string): boolean => {
    if (hasAdminAccess && Boolean(profile?.is_system_admin)) {
      return true;
    }

    if (!profile?.permissions) {
      return false;
    }

    for (const categoryPermissions of Object.values(profile.permissions)) {
      if (Array.isArray(categoryPermissions)) {
        const hasPerm = categoryPermissions.some((perm: { slug: string }) => perm.slug === permissionSlug);
        if (hasPerm) {
          return true;
        }
      }
    }

    return false;
  };

  const hasClientsAccess = hasPermission('global_access_clients');
  const hasProjectsAccess = hasPermission('global_access_projects');
  const hasCasesAccess = hasPermission('global_access_cases');
  const hasTasksAccess = hasPermission('global_access_work_tasks');
  const hasCalendarAccess = hasPermission('global_access_calendar');
  const hasReportsAccess = hasPermission('global_access_reports');

  const navItems = [
    ...(hasClientsAccess ? [{ id: 'clients', label: 'Клиенты', icon: '/icons/navigation/client.svg', path: '/clients' }] : []),
    ...(hasProjectsAccess ? [{ id: 'projects', label: 'Проекты', icon: '/icons/navigation/project.svg', path: '/projects' }] : []),
    ...(hasCasesAccess ? [{ id: 'cases', label: 'Дела', icon: '/icons/navigation/dela.svg', path: '/cases' }] : []),
    ...(hasTasksAccess ? [{ id: 'tasks', label: 'Задачи', icon: '/icons/navigation/zadachi.svg', path: '/tasks' }] : []),
    ...(hasCalendarAccess ? [{ id: 'calendar', label: 'Календарь', icon: '/icons/navigation/calendar.svg', path: '/calendar' }] : []),
    ...(hasReportsAccess ? [{ id: 'reports', label: 'Отчеты', icon: '/icons/navigation/reports.svg', path: '/reports' }] : []),
    ...(hasAdminAccess ? [{ id: 'admin', label: 'Администрирование', icon: '/icons/navigation/admin.svg', path: '/admin' }] : []),
  ];

  const isActive = (path: string) => {
    const { pathname } = location;
    if (path === '/dashboard') {
      return pathname === '/' || pathname === '/dashboard';
    }
    return pathname === path || pathname.startsWith(`${path}/`);
  };

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, closeMobileMenu]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobileMenu();
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen, closeMobileMenu]);

  const renderNavLinks = (linkClassName: string) =>
    navItems.map((item) => (
      <Link
        key={item.id}
        to={item.path}
        className={`${linkClassName} ${isActive(item.path) ? 'active' : ''}`}
        title={item.label}
        onClick={closeMobileMenu}
      >
        <span className="nav-icon">
          <img src={item.icon} alt="" />
        </span>
        <span className="nav-link-label">{item.label}</span>
      </Link>
    ));

  const renderDesktopUserActions = () => (
    <div className="dashboard-user">
      {isImpersonating && (
        <button
          type="button"
          className="dashboard-exit-impersonation-btn"
          onClick={() => setIsExitImpersonationModalOpen(true)}
          title="Выйти обратно в свой аккаунт"
          aria-label="Выйти обратно в свой аккаунт"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
        </button>
      )}
      <div
        className={`notification-icon ${isActive('/notifications') ? 'active' : ''}`}
        onClick={() => navigate('/notifications')}
        style={{ cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate('/notifications');
          }
        }}
      >
        <img src="/icons/header/notification.svg" alt="Уведомления" />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </div>
      <div
        className={`settings-icon ${isActive('/settings') ? 'active' : ''}`}
        onClick={() => navigate('/settings')}
        style={{ cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate('/settings');
          }
        }}
      >
        <img src="/icons/header/settings.svg" alt="Настройки" />
      </div>
      <div
        className={`user-profile ${isActive('/dashboard') ? 'active' : ''}`}
        onClick={() => navigate('/dashboard', { state: null })}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate('/dashboard', { state: null });
          }
        }}
        style={{ cursor: 'pointer' }}
      >
        <div className="user-avatar">
          <img src={avatar} alt={user?.name || profile?.name || 'Пользователь'} />
        </div>
        <span className="user-name">{user?.name || 'Пользователь'}</span>
      </div>
    </div>
  );

  const renderMobileMenuUserSection = () => (
    <div className="header-mobile-menu-user">
      {isImpersonating && (
        <button
          type="button"
          className="header-mobile-nav-link header-mobile-nav-link--danger"
          onClick={() => {
            closeMobileMenu();
            setIsExitImpersonationModalOpen(true);
          }}
        >
          <span className="nav-icon header-mobile-nav-icon-svg">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </span>
          Выйти из аккаунта
        </button>
      )}
      <button
        type="button"
        className={`header-mobile-nav-link ${isActive('/notifications') ? 'active' : ''}`}
        onClick={() => {
          navigate('/notifications');
          closeMobileMenu();
        }}
      >
        <span className="nav-icon">
          <img src="/icons/header/notification.svg" alt="" />
        </span>
        Уведомления
        {unreadCount > 0 && (
          <span className="header-mobile-nav-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>
      <button
        type="button"
        className={`header-mobile-nav-link ${isActive('/settings') ? 'active' : ''}`}
        onClick={() => {
          navigate('/settings');
          closeMobileMenu();
        }}
      >
        <span className="nav-icon">
          <img src="/icons/header/settings.svg" alt="" />
        </span>
        Настройки
      </button>
      <button
        type="button"
        className={`header-mobile-nav-link ${isActive('/dashboard') ? 'active' : ''}`}
        onClick={() => {
          navigate('/dashboard', { state: null });
          closeMobileMenu();
        }}
      >
        <span className="nav-icon nav-icon--avatar">
          <img src={avatar} alt="" />
        </span>
        <span className="header-mobile-nav-label">{user?.name || 'Пользователь'}</span>
      </button>
    </div>
  );

  return (
    <>
      <header className={`dashboard-header${isMobileMenuOpen ? ' dashboard-header--menu-open' : ''}`}>
        <Link to="/dashboard" className="dashboard-logo" style={{ cursor: 'pointer', textDecoration: 'none' }}>
          <img src="/logo.svg" alt="A2K CRM" className="logo-image" />
        </Link>

        <button
          type="button"
          className={`header-burger ${isMobileMenuOpen ? 'is-open' : ''}`}
          onClick={() => setIsMobileMenuOpen((open) => !open)}
          aria-expanded={isMobileMenuOpen}
          aria-controls="header-mobile-menu"
          aria-label={isMobileMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
        >
          <span className="header-burger-line" />
          <span className="header-burger-line" />
          <span className="header-burger-line" />
        </button>

        <nav className="dashboard-nav" aria-label="Основная навигация">
          {renderNavLinks('nav-link')}
        </nav>

        {renderDesktopUserActions()}

        <ExitImpersonationConfirmModal
          isOpen={isExitImpersonationModalOpen}
          onClose={() => setIsExitImpersonationModalOpen(false)}
          onConfirm={async () => {
            try {
              await stopImpersonating();
              window.location.href = '/dashboard';
            } catch (error) {
              console.error('Ошибка при возврате к своему аккаунту:', error);
              alert('Не удалось вернуться к своему аккаунту');
            }
          }}
          originalUserName={originalUser?.name || 'ваш аккаунт'}
        />
      </header>

      <div
        id="header-mobile-menu"
        className={`header-mobile-menu ${isMobileMenuOpen ? 'is-open' : ''}`}
        aria-hidden={!isMobileMenuOpen}
      >
        <button
          type="button"
          className="header-mobile-menu-overlay"
          onClick={closeMobileMenu}
          aria-label="Закрыть меню"
          tabIndex={isMobileMenuOpen ? 0 : -1}
        />
        <nav className="header-mobile-menu-panel" aria-label="Мобильная навигация">
          <div className="header-mobile-menu-head">
            <Link
              to="/dashboard"
              className="dashboard-logo"
              onClick={closeMobileMenu}
              style={{ cursor: 'pointer', textDecoration: 'none' }}
            >
              <img src="/logo.svg" alt="A2K CRM" className="logo-image" />
            </Link>
          </div>
          <div className="header-mobile-menu-body">
            <div className="header-mobile-menu-links">
              {renderNavLinks('header-mobile-nav-link')}
            </div>
            <div className="header-mobile-menu-divider" />
            {renderMobileMenuUserSection()}
          </div>
        </nav>
      </div>
    </>
  );
};

export default Header;
